import { z } from "zod";

/**
 * CROSS-NODE CACHE INVALIDATION.
 *
 * Every hot config read in the platform is served from a small per-process TTL cache — the
 * feature flags, the per-game ACTIVE/COMING_SOON statuses, the admin id set, the app
 * settings. That is what keeps them off the DB on the request/join path.
 *
 * The catch: a TTL cache is only ever *eventually* consistent. An admin flipping a game to
 * COMING_SOON on api node 1 left game-server nodes 2..N happily admitting players for the
 * rest of their window, and `GameGate`/`GameServerFlags` had no `invalidate()` at all — so
 * they could not be force-flushed even in principle. For a switch whose entire purpose is to
 * be an EMERGENCY STOP, "it takes effect somewhere between now and 30 seconds from now, on
 * some nodes" defeats the point.
 *
 * So an admin write now PUBLISHES the affected scope on this Redis channel, and every api +
 * game-server node drops the matching cache the moment it lands. The TTL stays exactly as it
 * was and remains the fallback: if Redis is down or a message is missed, the cache still
 * self-heals within its window. The channel makes the common case instant; the TTL makes the
 * failure case safe.
 */

/** The Redis pub/sub channel admin writes publish invalidations on. */
export const CACHE_INVALIDATION_CHANNEL = "cache-invalidation";

/**
 * What changed. Deliberately COARSE — one scope per cached thing, not per row: dropping a
 * five-row cache is far cheaper than reasoning about which key changed, and it means a new
 * cached field never needs a new message type.
 */
export const CACHE_SCOPES = ["flags", "games", "admins", "settings", "channels"] as const;
export type CacheScope = (typeof CACHE_SCOPES)[number];

export const cacheInvalidationSchema = z.object({
  scope: z.enum(CACHE_SCOPES),
  /** Publisher wall-clock (ms). Observability only — never used for ordering. */
  at: z.number().optional(),
});

export type CacheInvalidation = z.infer<typeof cacheInvalidationSchema>;

export function isCacheScope(value: unknown): value is CacheScope {
  return typeof value === "string" && (CACHE_SCOPES as readonly string[]).includes(value);
}
